'use client'
import { Fragment, useState, Children, ReactNode, ReactElement } from "react";
import Link from "next/link";
import useMount from "react-use/lib/useMount";
import styles from './bid.module.css';
import buttons from "components/_styled/buttons.module.css";
import wrappers from "components/_styled/wrappers.module.css"
import HeroSlider from "./HeroSlider";
import NavBar from "./NavBar";
import Intro from "./Intro";
import Properties from "./Properties";
import Schemes from "./Schemes";
import AboutBID from "./AboutBID";
import Agents from "./Agents";
import Snapshot from "./Snapshot";
import AboutDestination from "./AboutDestination";
import Links from "./Links";
import SimpleMap from "../SearchResults/SimpleMap";
import LazyImage from "components/LazyImage";

import logPageView from "lib/ga/pageView";
import HelpAndSupport from "./HelpAndSupport";

type Image = {
  title?: string;
  origin: string;
  src: string;
  alt: string;
  layout: string;
  objectFit: string;
  sources: {
    srcSet: string;
    type: string;
  }[];
};

// Define the rawImages parameter type
type RawImages = {
  title: string,
  format: {
    original: {
      uri: string
    }
  },
  id: number
}

const getThumbnail = (rawImages: RawImages): Image => {
  const image: Image  = {
    origin: "internal",
    src: `${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${rawImages?.format?.original?.uri}`,
    alt: rawImages?.title || "",
    layout: "fill",
    objectFit: "cover",
    sources: [
      {
        srcSet: "",
        type: "image/webp",
      },
      {
        srcSet: "",
        type: "image/jpg",
      },
    ],
  }
  const sizesJpg = ['w400', 'w600', 'w800', 'w1600', 'w1800'];
  const sizeswebp = ['w400wp', 'w600wp', 'w800wp', 'w1600wp', 'w1800wp'];
  for (const key in rawImages?.format) {
    if (rawImages.format.hasOwnProperty(key)) {
      if (sizeswebp.includes(key)) {
        image.sources[0].srcSet = `${image.sources[0].srcSet} ${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${rawImages.format[key].uri}  ${rawImages.format[key].width}w,`
      }

      if (sizesJpg.includes(key)) {
        image.sources[1].srcSet = `${image.sources[1].srcSet} ${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${rawImages.format[key].uri}  ${rawImages.format[key].width}w,`
      }
    }
  }
  return image;
};

type PropsVariableImage = {
  origin?: string, image: RawImages, lazy: boolean, priority: boolean
}

export const VariableImage = ({ origin, image, lazy = true, priority = false }: PropsVariableImage) => {
  const formatImage = getThumbnail(image)
  switch (origin) {
    case "internal":
      return <LazyImage {...formatImage} lazy={lazy} priority={priority} />;
    case "external":
      return <img {...formatImage} />;
  }
};

type PropsVariableLinkStyleWrap = {
  styledAs: string, children: string | ReactNode, origin?: string, themeFormat: {primary?: string, primaryText?: string}
}

const VariableLinkStyleWrap = ({ styledAs, children, themeFormat, ...otherProps }: PropsVariableLinkStyleWrap) => {
  switch (styledAs) {
    case "wrapper":
      return <a {...otherProps}>{children}</a>;
    default:
      return (
        <a className={buttons.SecondaryButton}
          style={{ backgroundColor: themeFormat.primary, color: themeFormat.primaryText, borderColor: themeFormat.primary }}
          {...otherProps}>
          {children}
        </a>
      );
  }
};

type PropsVariableLink = {
  origin: string | undefined, children: string | ReactNode, styledAs: string, themeFormat:  {
    primary?: string;
    primaryText?: string;
}, href: string
}

export const VariableLink = ({ origin, children, styledAs, themeFormat, ...linkProps }: PropsVariableLink) => {
  if (!origin) {
    if (Children.count(children) > 0) return <>{children}</>;
    else return null;
  }
  switch (origin) {
    case "internal":
      return (
        <Link {...linkProps} passHref={true} legacyBehavior>
          <VariableLinkStyleWrap styledAs={styledAs} themeFormat={themeFormat}>{children}</VariableLinkStyleWrap>
        </Link>
      );
    case "external":
      return (
        <VariableLinkStyleWrap origin={origin} styledAs={styledAs} themeFormat={themeFormat} {...linkProps}>
          {children}
        </VariableLinkStyleWrap>
      );
  }
};

export const Spans = (text: string, themeFormat: {primary: string}) => {
  const parts = text.split('[=');
  let spans: (ReactElement|string)[] = [];

  let count = 0;

  for (let part of parts) {
    const match = part.match(/^(\w*)(?:\/(\w+))?\](.*)/);
    if (match) {
      const text = match[1]
      const back = match[2]
      const body = match[3]
      let classes: string[] = [];
      if (text) {
        classes.push(`bid-text-${text}`)
      }
      if (back) {
        classes.push(`bid-back-${back}`)
      }
      if (classes.length) {
        const className = classes.join(' ');
        spans.push(<span key={count++} className={className} style={{ color: themeFormat.primary }}>{body}</span>)
      }
      else {
        spans.push(body);
      }
    }
    else {
      spans.push(part)
    }
  }
  return spans
}


const NewBID = (props) => {
  const { title, carousel, images, sections, links, tabs, metrics, logo, agents, uri, theme, metadata } = props.bid;
  const [menuVisible, setMenuVisible] = useState(false);
  const filterProperties = props.properties.results.filter((r) => r.docType === 'scheme' || r.docType === 'property');
  const filterSchemes = props.shoppingCenters.filter((r) => r.docType === 'scheme' || r.docType === 'property');
  useMount(() => {
    logPageView({ url: window.location.pathname + window.location.search });
  });
  const checkForMenuClose = (e) => {
    if (
      menuVisible &&
      e.path.filter((el) => (el.id && el.id === "cr-BID-menu") || (el.id && el.id === "cr-BID-menuButton")).length === 0
    )
      setMenuVisible(false);
  };
  const themeFormat = {
    primary: theme.primary ? theme.primary : '#f73261',
    primaryHeading: theme.dark ? theme.dark : '#f73261',
    primaryText: theme.light ? theme.light : '#f73261',
    secondary: theme.dark ? theme.dark : '#1c242b',
    secondaryHeading: theme.primary ? theme.primary : '#f73261',
    secondaryText: '#ffffff',
    secondaryImage: theme.secondary ? theme.secondary : '#fdf9d8',
  };
  return (
    <div className={wrappers.FluidWrapper}>
      <button style={{ backgroundColor: themeFormat.primary, color: themeFormat.primaryText }} className={styles.MenuButton} id="cr-BID-menuButton" onClick={(s) => setMenuVisible(!menuVisible)}>
        Explore {title}
      </button>
      <HeroSlider carousel={carousel} images={images} themeFormat={themeFormat} />

      <NavBar
        sections={sections}
        menuVisible={menuVisible}
        place={title}
        closeMenu={() => {
          setMenuVisible(false);
        }}
        themeFormat={themeFormat}
      />

      {sections.map((section, i) => (

        <Fragment key={i}>
          {section.type === 'introduction' && (
            <Intro title={Spans(section.title, themeFormat)} text={section.text} image={logo} />
          )}

          {section.type === 'links' && (
            <Links title={Spans(section.title, themeFormat)} links={links} themeFormat={themeFormat} />
          )}

          {section.type === 'tabs' && (
            <AboutDestination title={Spans(section.title, themeFormat)} data={tabs} images={images} themeFormat={themeFormat} />
          )}

          {section.type === 'metrics' && (
            <Snapshot metrics={metrics} theme={themeFormat} />
          )}

          {section.type === 'shopping_centres' && (
            <Schemes title={Spans(section.title, themeFormat)} selectedSchemes={filterSchemes} />
          )}

          {section.type === 'agents' && (
            <Agents title={Spans(section.title, themeFormat)} data={agents} themeFormat={themeFormat} />
          )}

          {section.type === 'properties' && (
            <Properties
              location={metadata.title}
              selectedProperties={filterProperties}
              totalProperties={props.properties.totalResults}
              url={uri}
              themeFormat={themeFormat}
            />
          )}
          {section.type === 'map' && (
            <div className={styles.MapContainer} id="map">
              <SimpleMap  boundary={props.boundary} theme={themeFormat} zoom={15} lat={Number(props.geo_location.lat)} lng={Number(props.geo_location.lon)} results={props.mapProperties} bidPage={true} locationDetails={null} />
            </div>
          )}
          {
            section.type === 'local_news' && props.news && (
              <AboutBID title={'Local News'} text={'Powered by Completely Retail News'} key={'aboutBID'}  posts={props.news} themeFormat={themeFormat} />
            )
          }

          {
            section.type === 'insights' && (
              <HelpAndSupport title={section.title} text={section.text} key={'help'} posts={props.helpAndSupport} />
            )
          }
        </Fragment>
      ))
      }

    </div>
  )
}

export default NewBID;

